import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Plus, Search, FileText, Copy, Trash2, Eye, Lock, Edit2, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { STANDARD_TEMPLATES, TEMPLATE_CATEGORIES, getCategoryMeta, buildPrintDocument, fillTemplate, resolveVariables } from '@/lib/documentTemplates';
import { useCompanySettings, useUpdateCompanySettings } from '@/api/queries/settings.queries';
import { selectCompany } from '@/store/slices/companySlice';
import { selectUser } from '@/store/slices/authSlice';
import { usePlan } from '@/hooks/usePlan';
import { toast } from 'sonner';

function PreviewModal({ template, onClose }) {
  const company  = useSelector(selectCompany);
  const user     = useSelector(selectUser);
  const vars     = resolveVariables(company, null, user);
  const filled   = fillTemplate(template.content_html, vars);
  const printDoc = buildPrintDocument(filled, template.name);

  const handlePrint = () => {
    const iframe = document.createElement('iframe');
    iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;';
    document.body.appendChild(iframe);
    iframe.contentWindow.document.open();
    iframe.contentWindow.document.write(printDoc);
    iframe.contentWindow.document.close();
    iframe.contentWindow.focus();
    setTimeout(() => { iframe.contentWindow.print(); document.body.removeChild(iframe); }, 500);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-[var(--bg-card)] rounded-2xl shadow-[var(--shadow-lg)] w-full max-w-4xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[var(--border)] shrink-0">
          <div>
            <h3 className="text-[15px] font-semibold text-[var(--text-primary)]">{template.name}</h3>
            <p className="text-[12px] text-[var(--text-tertiary)] mt-0.5">{template.description}</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={handlePrint}
              className="h-8 px-3 text-[12px] font-medium border border-[var(--border)] rounded-[8px] hover:bg-[var(--bg-hover)] text-[var(--text-secondary)] transition-colors">
              Print Preview
            </button>
            <button onClick={onClose}
              className="h-8 w-8 flex items-center justify-center rounded-[8px] hover:bg-[var(--bg-hover)] text-[var(--text-tertiary)]">
              ✕
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-auto p-6 bg-[var(--bg-app)]">
          <div className="max-w-[720px] mx-auto bg-white shadow-md rounded-lg overflow-hidden">
            <div dangerouslySetInnerHTML={{ __html: filled }} />
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateCard({ template, onPreview, onDuplicate, onDelete, onEdit }) {
  const cat = getCategoryMeta(template.category);

  return (
    <div className="bg-[var(--bg-card)] border border-[var(--border)] rounded-xl p-4 hover:shadow-[var(--shadow-sm)] transition-all hover:border-[var(--accent)]/30 group flex flex-col gap-3">
      {/* Icon + badge row */}
      <div className="flex items-start justify-between gap-2">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: cat.bg }}>
          <FileText size={18} style={{ color: cat.color }} />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-medium px-2 py-0.5 rounded-full"
            style={{ background: cat.bg, color: cat.color }}>
            {cat.label}
          </span>
          {template.is_standard && (
            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-[var(--bg-hover)] text-[var(--text-tertiary)] flex items-center gap-1">
              <Lock size={8} /> Standard
            </span>
          )}
        </div>
      </div>

      {/* Name + desc */}
      <div className="flex-1">
        <h3 className="text-[13px] font-semibold text-[var(--text-primary)] leading-tight">{template.name}</h3>
        <p className="text-[11px] text-[var(--text-tertiary)] mt-1 line-clamp-2 leading-relaxed">{template.description}</p>
      </div>

      {/* Variables preview */}
      <div className="flex flex-wrap gap-1">
        {(template.variables || []).slice(0, 4).map(v => (
          <span key={v} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-app)] text-[var(--text-tertiary)] font-mono border border-[var(--border)]">
            {`{{${v}}}`}
          </span>
        ))}
        {(template.variables || []).length > 4 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--bg-app)] text-[var(--text-tertiary)]">
            +{(template.variables || []).length - 4} more
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1.5 pt-1 border-t border-[var(--border)]">
        <button onClick={() => onPreview(template)}
          className="flex items-center gap-1.5 h-7 px-2.5 text-[11px] font-medium rounded-[6px] bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] transition-colors">
          <Eye size={11} /> Preview
        </button>
        {template.is_standard ? (
          <button onClick={() => onDuplicate(template)}
            className="flex items-center gap-1.5 h-7 px-2.5 text-[11px] font-medium rounded-[6px] border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-colors">
            <Copy size={11} /> Duplicate
          </button>
        ) : (
          <>
            <button onClick={() => onEdit(template)}
              className="flex items-center gap-1.5 h-7 px-2.5 text-[11px] font-medium rounded-[6px] border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] transition-colors">
              <Edit2 size={11} /> Edit
            </button>
            <button onClick={() => onDelete(template.id)}
              className="flex items-center gap-1.5 h-7 px-2.5 text-[11px] font-medium rounded-[6px] border border-red-200 text-red-500 hover:bg-red-50 transition-colors ml-auto">
              <Trash2 size={11} /> Delete
            </button>
          </>
        )}
        <button
          onClick={() => {/* use in issue */}}
          className="h-7 w-7 flex items-center justify-center rounded-[6px] border border-[var(--border)] text-[var(--text-tertiary)] hover:bg-[var(--bg-hover)] ml-auto transition-colors"
          title="Use this template">
          <ChevronRight size={12} />
        </button>
      </div>
    </div>
  );
}

export default function DocumentTemplatesPage() {
  const navigate  = useNavigate();
  const { hasFeature } = usePlan();
  const company   = useSelector(selectCompany);
  const user      = useSelector(selectUser);
  const { data: settingsData } = useCompanySettings();
  const { mutate: saveSettings } = useUpdateCompanySettings();

  const [search, setSearch]         = useState('');
  const [activeCategory, setActive] = useState('ALL');
  const [preview, setPreview]       = useState(null);

  const customTemplates = settingsData?.data?.settings?.document_templates || [];
  const allTemplates    = useMemo(() => [...STANDARD_TEMPLATES, ...customTemplates], [customTemplates]);

  const filtered = useMemo(() => {
    return allTemplates.filter(t => {
      const matchCat  = activeCategory === 'ALL' || t.category === activeCategory;
      const matchSrch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.description?.toLowerCase().includes(search.toLowerCase());
      return matchCat && matchSrch;
    });
  }, [allTemplates, activeCategory, search]);

  const handleDuplicate = (template) => {
    const copy = {
      ...template,
      id:          `tpl-custom-${Date.now()}`,
      name:        `${template.name} (Copy)`,
      is_standard: false,
    };
    const updated = [...customTemplates, copy];
    saveSettings({ settings: { document_templates: updated } }, {
      onSuccess: () => toast.success(`"${copy.name}" added to your templates`),
    });
  };

  const handleDelete = (id) => {
    if (!window.confirm('Delete this template? This action cannot be undone.')) return;
    const updated = customTemplates.filter(t => t.id !== id);
    saveSettings({ settings: { document_templates: updated } }, {
      onSuccess: () => toast.success('Template deleted'),
    });
  };

  const handleEdit = (template) => {
    navigate(`/documents/templates/${template.id}`);
  };

  const counts = useMemo(() => {
    const c = { ALL: allTemplates.length };
    TEMPLATE_CATEGORIES.forEach(cat => {
      c[cat.code] = allTemplates.filter(t => t.category === cat.code).length;
    });
    return c;
  }, [allTemplates]);

  if (!hasFeature('documents')) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-24 gap-4">
        <div className="w-16 h-16 rounded-2xl bg-[var(--bg-hover)] flex items-center justify-center">
          <Lock size={28} className="text-[var(--text-tertiary)]" />
        </div>
        <h2 className="text-[18px] font-semibold text-[var(--text-primary)]">Documents is a PRO feature</h2>
        <p className="text-[13px] text-[var(--text-secondary)] max-w-sm text-center">
          Upgrade to PRO to create and manage HR documents, issue letters, track materials, and run Full &amp; Final settlements.
        </p>
        <button onClick={() => navigate('/settings/plan')}
          className="h-9 px-5 bg-[var(--accent)] text-white text-[13px] font-medium rounded-[10px] hover:bg-[var(--accent-hover)] transition-colors">
          Upgrade to PRO
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-5">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-[20px] font-bold text-[var(--text-primary)]">Document Templates</h1>
          <p className="text-[13px] text-[var(--text-secondary)] mt-0.5">
            {allTemplates.length} templates — {STANDARD_TEMPLATES.length} standard, {customTemplates.length} custom
          </p>
        </div>
        <button onClick={() => navigate('/documents/templates/new')}
          className="flex items-center gap-2 h-9 px-4 bg-[var(--accent)] text-white text-[13px] font-medium rounded-[10px] hover:bg-[var(--accent-hover)] transition-colors shrink-0">
          <Plus size={15} /> New Template
        </button>
      </div>

      {/* Search + Category filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary)]" />
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search templates…"
            className="w-full h-9 pl-9 pr-3 text-[13px] bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent)] transition-colors"
          />
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {[{ code: 'ALL', label: 'All' }, ...TEMPLATE_CATEGORIES].map(cat => (
            <button key={cat.code}
              onClick={() => setActive(cat.code)}
              className={cn(
                'h-8 px-3 text-[12px] font-medium rounded-[8px] transition-all whitespace-nowrap',
                activeCategory === cat.code
                  ? 'bg-[var(--accent)] text-white'
                  : 'bg-[var(--bg-card)] border border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]',
              )}>
              {cat.label} <span className="ml-1 opacity-70">({counts[cat.code] || 0})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 gap-3">
          <FileText size={32} className="text-[var(--text-tertiary)]" />
          <p className="text-[14px] font-medium text-[var(--text-secondary)]">No templates found</p>
          <p className="text-[12px] text-[var(--text-tertiary)]">Try a different category or create a new template</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onPreview={setPreview}
              onDuplicate={handleDuplicate}
              onDelete={handleDelete}
              onEdit={handleEdit}
            />
          ))}
        </div>
      )}

      {/* Preview modal */}
      {preview && <PreviewModal template={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}
